'use client';

import Icon from '@ant-design/icons';
import { MIND_MODE, WS_MIND_TYPE } from '@mindverse/accessor-open';
import { generatePath } from '@remix-run/router';
import { message } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import DiscoverFillSvg from '@/components/Icon/svgs/discover_fill.svg';
import App, { EAppMode } from '@/components/chat-app';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import type { EventCreateSessionInterface } from '@/components/chat-app/utils/utils';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import HomeContext from '@/context/HomeContext';
import { useChatManager } from '@/hooks/useChatManager';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import type { ICircleConversation, ICircleSettingDetail } from '@/service/friend';
import { EConversationType, getCircleSettingDetail } from '@/service/friend';
import useFriendStore from '@/store/useFriendStore';
import { ROUTER_PATH } from '@/utils/router';

import BackIcon from '../HomePage/BackIcon';
import CircleChatContext from './CircleChatContext';
import MoreIcon from './CircleChatMoreIcon';
import styles from './index.less';

const defaultImage =
  'https://mindverseglobal.mindverse.com/front-img/crm/1766814853894/defaultImage.png';

interface IProps {
  route: string;
  className?: string;
  forceInMobile?: boolean;
}

const CircleChat = (props: IProps): JSX.Element => {
  const { route, className, forceInMobile } = props;

  const { sessionSocketManager } = useChatManager();
  const detailInfo = useGenius();
  const { shadowMindId: mindId } = detailInfo;
  const { voiceMute } = useContext(HomeContext);
  const { userProfile } = useModel('auth-model', (model) => ({ userProfile: model.userProfile }));
  const [circleConversation, setCircleConversation] = useState<ICircleConversation | null>(null);

  const conversationList = useFriendStore((state) => state.conversationList);

  useEffect(() => {
    const tempCircleConversation = conversationList?.conversations.find(
      (item): item is ICircleConversation =>
        item.conversationType === EConversationType.CIRCLE && item.circle.route === route
    );

    setCircleConversation(tempCircleConversation || null);
  }, [conversationList, route]);

  const [layoutSize] = useStorage('layoutSize');
  const [isClient, setIsClient] = useState(false);
  const { t } = useTranslation();

  const [circleDetail, setCircleDetail] = useState<ICircleSettingDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const layoutContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Fetch circle detail by route (后台刷新完整数据)
  useEffect(() => {
    if (route) {
      getCircleSettingDetail({ route })
        .then((res) => {
          if (res.data.code === 0) {
            setCircleDetail(res.data.data);
          } else {
            message.error(res.data.message || t('error.oops'));
          }
        })
        .catch((err) => {
          message.error(err.message || t('error.oops'));
        });
    }
  }, [route, t]);

  const inMobile = useMemo(
    () =>
      typeof forceInMobile === 'boolean'
        ? forceInMobile
        : isClient && layoutSize === LAYOUT_SIZE.SMALL,
    [layoutSize, isClient, forceInMobile]
  );

  // 创建 session
  useEffect(() => {
    if (route && mindId && sessionSocketManager && !!circleConversation?.circle.sessionId) {
      setLoading(true);
      // 延迟确保 App 组件中的 useChatSession 事件监听器已注册
      const timer = setTimeout(() => {
        dispatchEvent(
          new CustomEvent<EventCreateSessionInterface>(CONTAINER_EVENT.CREATE_SESSION, {
            detail: {
              params: {
                originSessionId: circleConversation.circle.sessionId,
                newSession: false,
                extParams: {
                  sessionType: 'CIRCLE'
                }
              },
              callback: () => {
                setLoading(false);
              }
            }
          })
        );
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [route, mindId, sessionSocketManager, circleConversation?.circle.sessionId]);

  if (!mindId) {
    return <div className={classNames(styles.container, className)} />;
  }

  if (!isClient) {
    return (
      <div className={classNames(styles.container, className)}>
        <LottieJumpLoading className="!w-16 !h-16 !mt-[24%]" />
      </div>
    );
  }

  return (
    <CircleChatContext.Provider
      value={{
        route,
        circleDetail,
        setCircleDetail
      }}
    >
      <div className={classNames(styles.container, className)}>
        <div className={classNames(styles.headerContainer, { [styles.inMobile]: inMobile })}>
          <BackIcon className={styles.voiceIcon} iconClassName="!text-[#FEFDFF]" />
          <div className={styles.circleInfo}>
            <img
              alt={circleDetail?.name}
              className={styles.circleAvatar}
              src={circleDetail?.icon || defaultImage}
            />
            {circleDetail ? (
              <div className={styles.circleText}>
                <span className={styles.circleName}>{circleDetail.name}</span>
                <span className={styles.circleMemberCount}>
                  {circleDetail.memberCount}
                  {t('circle.members_unit')}
                </span>
              </div>
            ) : null}
          </div>

          <Icon
            className={classNames(styles.voiceIcon, 'ml-auto')}
            component={DiscoverFillSvg}
            onClick={() => {
              window.open(generatePath(ROUTER_PATH.group, { route }), '_blank');
            }}
          />
          <MoreIcon
            circleConversation={circleConversation}
            className={styles.voiceIcon}
            iconClassName="!text-[#FEFDFF]"
          />
        </div>
        <div
          ref={layoutContainerRef}
          className={classNames(styles.layoutContainer, { [styles.inMobile]: inMobile })}
          id="circle-chat-layout-container"
        >
          {loading && <LottieJumpLoading className="!w-16 !h-16 !mt-[24%]" />}
          <div className={classNames(styles.contentContainer, { [styles.inMobile]: inMobile })}>
            {/* 保留占位，后续补充内容 */}
          </div>
          <App
            chatContainer={{
              appMode: inMobile ? EAppMode.CHAT_MOBILE : EAppMode.CHAT_MODE,
              hideQuickNote: true,
              autoCreateSession: false,
              chatType: 'circle',
              config: {
                mindConfig: {
                  mindId: mindId,
                  mindType: WS_MIND_TYPE.shadow,
                  mode: MIND_MODE.PERSONAL
                },
                userId: userProfile?.userId,
                sessionManager: sessionSocketManager,
                voiceMute: voiceMute
              }
            }}
            className={classNames(styles.chatContainer)}
          />
        </div>
      </div>
    </CircleChatContext.Provider>
  );
};

export default CircleChat;
